import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, LabelM } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_MISSIONS_CATEGORY_RARITY,
  ANY_SPORT_PLAY_SERIES,
  ANY_SPORT_SERIES_MISSIONS_PLAY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary } from '@sorare/core/src/lib/glossary';

import { MissionToastBackground } from '../MissionToastBackground';
import type { DailyBoostToastContent_task } from './__generated__/index.graphql';

type Props = { task: DailyBoostToastContent_task; onClose: () => void };
export const DailyBoostToastContent = ({ task, onClose }: Props) => {
  const { parentTask, title } = task;
  const { generateSportContextPath } = useSportContext();
  const {
    flags: { enableSeriesOnboardingPacks = false },
  } = useFeatureFlags();
  const isDesktop = useIsDesktopAndAbove();

  let to = generateSportContextPath(ANY_SPORT_MISSIONS_CATEGORY_RARITY, {
    params: {
      rarity: Rarity.common.toLowerCase(),
      category: 'play',
    },
  });

  if (enableSeriesOnboardingPacks) {
    if (isDesktop) {
      to = generateSportContextPath(ANY_SPORT_PLAY_SERIES);
    } else {
      to = generateSportContextPath(ANY_SPORT_SERIES_MISSIONS_PLAY);
    }
  }

  return (
    <Link to={to} onClick={onClose}>
      <MissionToastBackground color="#7029FF" showHalo>
        <SBHorizontal>
          <Vertical>
            <BrandXS color="var(--c-nd-600)">{title}</BrandXS>
            <LabelM bold>{parentTask?.title}</LabelM>
          </Vertical>
          <Button color="primary" size="compact" asSpan>
            <FormattedMessage {...glossary.claim} />
          </Button>
        </SBHorizontal>
      </MissionToastBackground>
    </Link>
  );
};

DailyBoostToastContent.fragments = {
  task: gql`
    fragment DailyBoostToastContent_task on Task {
      id
      title
      parentTask {
        id
        title
      }
    }
  ` as TypedDocumentNode<DailyBoostToastContent_task>,
};
